<template>
  <div class="w-full h-full relative bg-black flex flex-col" :class="{ recording: isRecording }">
    <step
      :step="currentStep"
      :index="index"
      :disabled="currentStep.needCheck"
      :hide-button="index === 3"
      @next="next"
    />
    <spirit-level v-show="index === 0" :level="orientationLevel" @passed="onSpiritLevelPassed" />
  </div>
</template>

<script>
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import { wait } from '@/modules/utils';
import ScanProcess from '@/modules/scan-process';
import audioPlayer from '@/mixins/audio-player';
import Step from '@/components/MobileScan/Scan/Step.vue';
import SpiritLevel from '@/components/MobileScan/Scan/SpiritLevel.vue';


export default {
  name: 'Scan',
  components: {
    Step,
    SpiritLevel,
  },
  mixins: [audioPlayer],
  data: vm => ({
    index: 0,
    steps: [
      { info: vm.$t('mobileScan.vertical_info'), needCheck: true },
      { info: vm.$t('mobileScan.inscreen_info') },
      { info: vm.$t('mobileScan.backoff_info'), needCheck: false },
      { info: vm.$t('mobileScan.spin_info') },
    ],
    orientationLevel: 0,
    startBackoff: false,
    isRecording: false,
    isProcessing: false,
    countdown: 0,
    videoSrc: '',
  }),
  mounted() {
    this.$events.on(HW_EVENTS.SPIRIT_LEVEL, this.onSpiritLevel);
    this.$events.on(HW_EVENTS.MOBILE_SCAN_SCAN_ERROR, this.onScanError);
    this.$events.on(HW_EVENTS.MOBILE_SCAN_PREVIEW_VIDEO_SOURCE, this.onPreviewVideo);
    this.$events.emit(HW_EVENTS.START_SPIRIT_LEVEL_DETECTION);
    this.playAudio('ms-10-scan_vertical', true);
  },
  beforeDestroy() {
    this.$events.off(HW_EVENTS.SPIRIT_LEVEL, this.onSpiritLevel);
    this.$events.off(HW_EVENTS.MOBILE_SCAN_SCAN_ERROR, this.onScanError);
    this.$events.off(HW_EVENTS.MOBILE_SCAN_PREVIEW_VIDEO_SOURCE, this.onPreviewVideo);
  },
  methods: {
    onSpiritLevel(level) {
      this.orientationLevel = level;
    },
    onSpiritLevelPassed(passed) {
      this.steps[0].needCheck = !passed;
    },
    launchMobileScan() {
      this.$events.emit(HW_EVENTS.LAUNCH_MOBILE_SCAN, {
        userHeight: ScanProcess.mobileScanHeight,
      });
    },
    abort() {
      this.$events.emit(HW_EVENTS.CLOSE_MOBILE_SCAN);
      this.$events.goto('HomePage');
    },
    onScanError(error) {
      console.log('onScanError', error);
      this.abort();
    },
    onPreviewVideo(url) {
      console.log('onPreviewVideo', url);
      this.isProcessing = false;
      this.videoSrc = Array.isArray(url) ? url[0] : url;
      this.next();
    },
    next() {
      const { index } = this;
      if (index === 2) {
        // start backoff countdown before next
        this.startBackoff = !this.startBackoff;
        this.steps[index].needCheck = this.startBackoff;
        if (this.startBackoff) {
          const backoffTime = 10;
          this.$events.emit(HW_EVENTS.START_MOBILE_SCAN_BACKOFF_COUNTDOWN, {
            countdown: backoffTime,
            backoffText: this.$t('mobileScan.starting_in'),
          });
          Promise.all([
            this.startCountDown(backoffTime),
            wait(3).then(() => this.playAudio('ms-08-arms_open', true)),
          ]).then(() => this.next());
          return;
        }
      }
      const nextIndex = index + 1;
      if (!this.steps[nextIndex]) {
        this.done();
      } else {
        this.index = nextIndex;
        if (nextIndex === 1) {
          this.$events.emit(HW_EVENTS.STOP_SPIRIT_LEVEL_DETECTION);
          this.launchMobileScan();
        } else if (nextIndex === 3) {
          this.playAndWait('ms-13-scan_start', 0.5, true).then(() => {
            this.startRecording();
          });
        }
      }
    },
    done() {
      this.$events.goto('MobileScan/Preview', { params: { video: this.videoSrc } });
    },
    startRecording() {
      this.isRecording = true;
      const duration = 10;
      this.$events.emit(HW_EVENTS.START_MOBILE_SCAN_RECORDING, duration);
      this.playAudio('ms-14-scan_countdown', true);
      this.startCountDown(duration).then(() => this.stopRecording());
    },
    stopRecording() {
      this.$events.emit(HW_EVENTS.STOP_MOBILE_SCAN_RECORDING);
      this.isRecording = false;
      this.isProcessing = true;
    },
    startCountDown(countdown) {
      this.countdown = countdown;
      return wait(1).then(() =>
        countdown > 0 ? this.startCountDown(countdown - 1) : Promise.resolve()
      );
    },
  },
  computed: {
    currentStep() {
      return this.steps[this.index];
    },
  },
  watch: {
    index(nv) {
      if (nv === 1) {
        this.playAudio('ms-11-scan_in_screen', true);
      } else if (nv === 2) {
        this.playAudio('ms-12-scan_back_off', true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.recording {
  background: linear-gradient(90deg, #540DEB 11.03%, #2245FF 83.45%);
}
</style>
